import { CONFIG } from '../config.js';
import { generateUUID } from '../utils/uuid.js';
import { changeAnswerOptionsSequence, resolveImageUrl } from '../utils/helpers.js';
import { getStyleData } from '../styles/style-generator.js';
import { createQuestion } from './question-factory.js';
import { checkRouteLogic } from './route-logic.js';
import { AnswerManager } from './answer-manager.js';
import { viewSurvey, viewQuestion, respondQuestion, completeSurvey, trackBehavior } from '../api/backend.js';
import {
    normalizeSessionId,
    getStoredSessionId,
    storeSessionId,
    clearStoredSessionId,
    buildSurveyViewPayload,
} from './survey-session.js';

export class SurveyEngine {
    // Kept on window so multiple embedded copies of the widget script
    // (separate IIFE bundles) share one dedup set
    static get activeSurveys() {
        if (!window.__POLLHERO_ACTIVE_SURVEYS__) {
            window.__POLLHERO_ACTIVE_SURVEYS__ = new Set();
        }
        return window.__POLLHERO_ACTIVE_SURVEYS__;
    }

    // Minimum visible fraction before view tracking fires; surveys taller than
    // the viewport (small screens) never reach a ratio of 1
    static VIEW_THRESHOLD = 0.1;

    // Do not wait indefinitely for a container rendered by a client-side app.
    static CSS_SELECTOR_WAIT_TIMEOUT = 10000;

    constructor(surveyData, surveyType = 'onsite', currentIndex = 0, parentNode = null, storage = null, can = {}, options = {}) {
        // forceExpanded: never start minimized (used by the question preview tab)
        this.forceExpanded = options.forceExpanded === true;
        // staticLayout: preview renders the box in normal flow instead of
        // positioning it inside the pane (the embedding component places it)
        this.staticLayout = options.staticLayout === true;
        // previewType: which survey type the preview mimics (e.g. 'embed');
        // embed previews center the box and never show close/minimize
        this.previewType = typeof options.previewType === 'string' ? options.previewType : null;
        if (this.previewType === 'embed' || surveyType === 'previewCssSelectorEmbed') {
            this.forceExpanded = true;
        }
        this.surveyData = surveyData;
        this.questions = surveyData.questions;
        this.currentIndex = currentIndex;
        this.currentQuestionInstance = null;
        this.questionHistory = [];
        this.startIndex = 0;
        this.surveyStartTime = Date.now();
        this.surveyId = surveyData.survey_id;
        this.isPreviewMode = false;
        this.surveyType = surveyType;
        this.isRedirectedDirectlyToThankYou = false;

        if (SurveyEngine.activeSurveys.has(this.surveyId)) {
            this.isDuplicate = true;
            return;
        }

        if (surveyType !== 'preview') {
            SurveyEngine.activeSurveys.add(this.surveyId);
        }
        
        this.shadowRoot = null;
        this.observer = null;
        this.can = can || {};
        this.answersLength = 0;
        this.storage = storage;

        // Instance-level state (was global)
        this.elementIds = [];
        this.nextAndSkipButtonBlockIds = [];

        // Deduplication for new API
        this.viewedQuestionIds = new Set();
        this.hasCalledComplete = false;
        this.answersInitialized = false;
        this.surveyViewSent = false;

        // Backend session of this participation. The launcher passes the id
        // from the surveys/view answer; otherwise fall back to the one
        // remembered for this survey in sessionStorage (embeds, SPA reloads).
        // viewTracked: surveys/view was already awaited before construction.
        this.viewTracked = options.viewTracked === true;
        this.sessionId = normalizeSessionId(options.sessionId) ?? getStoredSessionId(this.surveyId);
        // displayGate: embeds ask the server (surveys/view) only once their
        // container is mounted and inside the viewport. The launcher passes the
        // request; startSurvey() runs it and aborts on display: false.
        this.displayGate = typeof options.displayGate === 'function' ? options.displayGate : null;
        this.displayGateStarted = false;
        // onDisplayDecision(true|false): tells the launcher whether this engine
        // stays (shown, badge rendered or waiting for the viewport) or aborted
        this.onDisplayDecision = typeof options.onDisplayDecision === 'function' ? options.onDisplayDecision : null;

        // Answer manager
        this.answerManager = new AnswerManager();

        // Store the previously focused element for focus restoration
        this.previouslyFocusedElement = document.activeElement;

        // A host created here is removed again when the survey aborts; a host
        // handed in (<poll-hero>) belongs to the page
        this.ownsHostElement = !parentNode;

        if (parentNode) {
            this.hostElement = parentNode;
        } else {
            this.hostElement = document.createElement('div');
            this.hostElement.classList.add('ph-survey-host');
            this.hostElement.style.display = 'flex';
            if (this.isCssSelectorEmbed()) {
                if (this.surveyData.style?.widthValue && this.surveyData.style?.widthUnit) {
                    this.hostElement.style.width = `${this.surveyData.style?.widthValue}${this.surveyData.style?.widthUnit}`;
                }
                this.hostElement.style.maxWidth = '100%';
                const position = this.surveyData.style?.position || 'middle';
                if (position === 'middle' || position === 'center') {
                    this.hostElement.style.marginLeft = 'auto';
                    this.hostElement.style.marginRight = 'auto';
                } else if (position === 'right') {
                    this.hostElement.style.marginLeft = 'auto';
                    this.hostElement.style.marginRight = '0';
                } else if (position === 'left') {
                    this.hostElement.style.marginLeft = '0';
                    this.hostElement.style.marginRight = 'auto';
                }
            }
            // CSS-selector embeds are mounted into their target directly. Keeping
            // the host detached while we wait prevents an empty node in <body>.
            // A gated popup is attached once something is rendered (attachHost).
            if (!this.isCssSelectorEmbed() && !this.displayGate) {
                document.body.appendChild(this.hostElement);
            }
        }

        this.brandingLogo = resolveImageUrl(surveyData.logo);
        surveyData.avatar
            ? (this.avatar = `<image src='${resolveImageUrl(surveyData.avatar)}' width="44px" height="44px" class="ph-avatar"></image>`)
            : (this.avatar = null);

        this.userId = storage ? storage.get('ph_user_id') || generateUUID() : generateUUID();

        switch (this.surveyType) {
            case 'onsite':
                this.getPopupSurveyContainer();
                break;
            case 'embedded':
            case 'embed':
                this.getEmbedSurveyContainer();
                break;
            case 'cssSelectorEmbed':
            case 'previewCssSelectorEmbed':
                this.isPreviewMode = this.surveyType === 'previewCssSelectorEmbed';
                this.getCssSelectorEmbedSurveyContainer();
                break;
            case 'standalone':
                this.getStandaloneSurveyContainer();
                break;
            case 'previewStandalone':
                this.isPreviewMode = true;
                this.getStandaloneSurveyContainer();
                break;
            case 'preview':
                this.isPreviewMode = true;
                this.getPreviewSurveyContainer();
                break;
            case 'previewLink':
                this.isPreviewMode = true;
                this.getPopupSurveyContainer();
                break;
            default:
                this.getPopupSurveyContainer();
        }

        // A CSS-selector target can be rendered after this script (for example by
        // an SPA). Its observer resumes initialization after the host is mounted.
        if (this.pendingInjection) return;

        this.startSurvey();
    }

    startSurvey() {

        if (this.injectionFailed) {
            // Container injection failed (e.g. CSS selector not found): abort
            // cleanly so the survey neither crashes init() nor leaks state
            this.abortSurvey();
            return;
        }

        if (this.displayGate && this.gateWaitsForViewport() && !this.displayGateStarted) {
            this.displayGateStarted = true;
            // The launcher gets its engine now; a denied gate aborts later
            this.reportDisplayDecision(true);
            this.whenVisible(this.displayGateTarget(), 0, () => this.runDisplayGate(() => this.startSurvey()));
            return;
        }

        // The server confirmed the display: now the replaced target may lose
        // its previous content
        this.commitReplace();

        this.setInitialIndex();

        const style = this.surveyData.style;
        if (style.allowCloseMinimize === true || style.allowCloseMinimize === '1') {
            style.canBeClosed = true;
        } else if (style.allowCloseMinimize === false || style.allowCloseMinimize === '0') {
            style.canBeClosed = false;
        }

        if (this.surveyType === 'preview') {
            style.keepMinimizedOnPageChange = false;
        }
        
        let shouldStartMinimized = style.minimizeAtStart === true || style.minimizeAtStart === '1';
        if (style.keepMinimizedOnPageChange === true || style.keepMinimizedOnPageChange === '1') {
            const currentUrl = window.location.href;
            if (!sessionStorage.getItem(`ph_survey_entry_url_${this.surveyId}`)) {
                sessionStorage.setItem(`ph_survey_entry_url_${this.surveyId}`, currentUrl);
            } else if (sessionStorage.getItem(`ph_survey_entry_url_${this.surveyId}`) !== currentUrl) {
                sessionStorage.setItem(`ph_survey_redirected_${this.surveyId}`, 'true');
            }

            const lastUrl = sessionStorage.getItem(`ph_survey_last_url_${this.surveyId}`);
            sessionStorage.setItem(`ph_survey_last_url_${this.surveyId}`, currentUrl);
            
            const isUrlChange = lastUrl && lastUrl !== currentUrl;
            
            if (isUrlChange) {
                shouldStartMinimized = true;
            } else if (this.storage && this.storage.get(`ph_survey_minimized_${this.surveyId}`)) {
                shouldStartMinimized = true;
            }
        }

        // A standalone preview is always shown from the start (no visibility redirect)
        if (this.isStandalone() && this.storage && !this.isPreviewMode) {
            const visibility = this.surveyData.survey_settings?.visibility || 'always';
            const completedSurveys = this.storage.get('ph_survey_completed') || [];
            const viewedSurveys = this.storage.get('ph_survey_viewed') || [];
            const hasAtLeastOneAnswerSurveys = this.storage.get('ph_survey_at_least_one_answer') || [];

            let shouldGoToThankYou = false;
            if (visibility === 'always') {
                shouldGoToThankYou = false;
            } else if (visibility === 'untilSubmitted') {
                shouldGoToThankYou = completedSurveys.includes(this.surveyId);
            } else if (visibility === 'once') {
                shouldGoToThankYou = viewedSurveys.includes(this.surveyId);
            } else if (visibility === 'atLeastOneAnswer') {
                shouldGoToThankYou = hasAtLeastOneAnswerSurveys.includes(this.surveyId);
            }

            if (shouldGoToThankYou) {
                let thankYouIndex = -1;
                for (let i = this.questions.length - 1; i >= 0; i--) {
                    const q = this.questions[i];
                    const type = q.properties ? q.properties.type : q.type;
                    if (type === 'Thank You Message') {
                        thankYouIndex = i;
                        break;
                    }
                }
                if (thankYouIndex !== -1) {
                    this.currentIndex = thankYouIndex;
                    this.startIndex = thankYouIndex;
                    this.isRedirectedDirectlyToThankYou = true;
                }
            }
        }

        if (!this.forceExpanded && 
            (this.isPreviewMode || this.surveyType === 'onsite') &&
            (this.surveyData.style.canBeMinimized === true || this.surveyData.style.canBeMinimized === '1' ||
            ((this.surveyData.style.allowCloseMinimize === true || this.surveyData.style.allowCloseMinimize === '1') && shouldStartMinimized))) {
            // Minimized start: no view until the visitor opens the badge
            this.renderMiniIcon();
        } else {
            this.openSurvey(() => this.init());
        }
    }

    isCssSelectorEmbed() {
        return this.surveyType === 'cssSelectorEmbed' || this.surveyType === 'previewCssSelectorEmbed';
    }

    // Nothing of the survey is shown: free the id so a later launch may try
    // again and remove the host we created (never the page's own element)
    abortSurvey() {
        SurveyEngine.activeSurveys.delete(this.surveyId);
        if (this.ownsHostElement) {
            this.hostElement.remove();
        }
    }

    isEmbedType() {
        return this.surveyType === 'embed' || this.surveyType === 'embedded' || this.isCssSelectorEmbed();
    }

    // Embedded containers may sit below the fold; a popup is always on screen
    gateWaitsForViewport() {
        return this.isEmbedType();
    }

    // The element whose appearance in the viewport triggers the display gate.
    // A replaced target keeps its old content until the gate passes, so the
    // (still empty) host sits below that content: observing the host would
    // only fire after the visitor scrolled past the whole element. The target
    // itself intersects as soon as its first pixel shows.
    displayGateTarget() {
        return this.replaceTarget || this.hostElement;
    }

    reportDisplayDecision(shown) {
        const callback = this.onDisplayDecision;
        if (!callback) return;
        this.onDisplayDecision = null;
        callback(shown);
    }

    // A host created by the engine joins the page only when something of the
    // survey is rendered (badge or questions), never for a pending gate
    attachHost() {
        if (this.ownsHostElement && !this.hostElement.isConnected) {
            document.body.appendChild(this.hostElement);
        }
    }

    // Asks the server (surveys/view) and continues with onAllowed on
    // display: true; anything else aborts the survey. Nothing of the survey
    // counts as viewed before this.
    runDisplayGate(onAllowed) {
        const gate = this.displayGate;
        this.displayGate = null;
        this.displayGateStarted = true;

        let pending;
        try {
            pending = Promise.resolve(gate());
        } catch (error) {
            pending = Promise.reject(error);
        }

        pending
            .then((result) => {
                if (!result || !result.display) {
                    this.abortSurvey();
                    this.reportDisplayDecision(false);
                    return;
                }
                this.sessionId = normalizeSessionId(result.sessionId);
                this.viewTracked = true;
                onAllowed();
                this.reportDisplayDecision(true);
            })
            .catch((error) => {
                console.warn('[Survey] Failed to start survey:', this.surveyId, error);
                this.abortSurvey();
                this.reportDisplayDecision(false);
            });
    }

    // Opens the survey (initial render or badge click). With a pending gate
    // the server is asked first, so a minimized start only counts as a view
    // once the visitor actually opens it.
    openSurvey(render) {
        const show = () => {
            this.attachHost();
            render();
        };
        if (this.displayGate) {
            this.runDisplayGate(show);
            return;
        }
        show();
    }

    // Runs the callback once the target shows at least the given fraction in
    // the viewport (immediately when IntersectionObserver is unavailable)
    whenVisible(target, threshold, callback) {
        if (!target || typeof IntersectionObserver === 'undefined') {
            callback();
            return;
        }

        const io = new IntersectionObserver((entries, observer) => {
            const entry = entries[0];
            if (entry && entry.isIntersecting) {
                observer.disconnect();
                callback();
            }
        }, { threshold });

        io.observe(target);
    }

    isLinkPreview() {
        return this.surveyType === 'previewLink' || this.surveyType === 'previewCssSelectorEmbed';
    }

    isStandalone() {
        return this.surveyType === 'standalone' || this.surveyType === 'previewStandalone';
    }

    createContext() {
        return {
            shadowRoot: this.shadowRoot,
            surveyType: this.surveyType,
            previewType: this.previewType,
            avatar: this.avatar,
            can: this.can,
            language: this.surveyData.language || 'de',
            surveyId: this.surveyId,
            brandingLogo: this.brandingLogo,
            questions: this.questions,
            currentIndex: this.currentIndex,
            startIndex: this.startIndex,
            style: this.surveyData.style,
            settings: this.surveyData.settings,
            elementIds: this.elementIds,
            nextAndSkipButtonBlockIds: this.nextAndSkipButtonBlockIds,
            isPreviewMode: this.isPreviewMode,
            goNext: (question, answer) => this.goNext(question, answer),
            trackBehavior: (question, behavior) => this.trackBehavior(question, behavior),
            goBack: () => this.goBack(),
            skip: (skipped) => this.skip(skipped),
            closeSurvey: () => this.closeSurvey(),
            closeOverlay: () => this.closeOverlay(),
            updateAnswers: (question, answer) => this.updateAnswers(question, answer),
            getById: (root, id) => this.getById(root, id),
            changeAnswerOptionsSequence: changeAnswerOptionsSequence,
            isRedirectedDirectlyToThankYou: this.isRedirectedDirectlyToThankYou,
        };
    }

    updateSurveyData(newSurveyData, newCurrentIndex) {
        if (!this.hostElement) return;

        this.surveyData = newSurveyData;
        this.questions = newSurveyData.questions;
        this.currentIndex = newCurrentIndex;
        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = '';
        }

        this.brandingLogo = resolveImageUrl(this.surveyData.logo);
        this.surveyData.avatar
            ? (this.avatar = `<image src='${resolveImageUrl(this.surveyData.avatar)}' width="44px" height="44px" class="ph-avatar"></image>`)
            : (this.avatar = null);

        this.getPreviewSurveyContainer();

        const style = this.surveyData.style;
        if (style.allowCloseMinimize === true || style.allowCloseMinimize === '1') {
            style.canBeClosed = true;
        } else if (style.allowCloseMinimize === false || style.allowCloseMinimize === '0') {
            style.canBeClosed = false;
        }

        if (this.surveyType === 'preview') {
            style.keepMinimizedOnPageChange = false;
        }

        let shouldStartMinimized = style.minimizeAtStart === true || style.minimizeAtStart === '1';

        if (style.keepMinimizedOnPageChange === true || style.keepMinimizedOnPageChange === '1') {
            const currentUrl = window.location.href;
            if (!sessionStorage.getItem(`ph_survey_entry_url_${this.surveyId}`)) {
                sessionStorage.setItem(`ph_survey_entry_url_${this.surveyId}`, currentUrl);
            } else if (sessionStorage.getItem(`ph_survey_entry_url_${this.surveyId}`) !== currentUrl) {
                sessionStorage.setItem(`ph_survey_redirected_${this.surveyId}`, 'true');
            }

            const lastUrl = sessionStorage.getItem(`ph_survey_last_url_${this.surveyId}`);
            sessionStorage.setItem(`ph_survey_last_url_${this.surveyId}`, currentUrl);
            
            const isUrlChange = lastUrl && lastUrl !== currentUrl;
            
            if (isUrlChange) {
                shouldStartMinimized = true;
            } else if (this.storage && this.storage.get(`ph_survey_minimized_${this.surveyId}`)) {
                shouldStartMinimized = true;
            }
        }

        if (!this.forceExpanded &&
            (style.allowCloseMinimize === true || style.allowCloseMinimize === '1') && shouldStartMinimized) {
            this.renderMiniIcon();
        } else {
            this.init();
        }
    }

    getById(root, id) {
        if (root instanceof ShadowRoot || root instanceof Document) {
            return root.getElementById(id);
        }
        return root.querySelector(`#${CSS.escape(id)}`);
    }

    addObserver() {
        this.observer = new MutationObserver(() => {
            this.elementIds.forEach((id) => {
                const element = this.getById(this.shadowRoot, id);
                if (element) {
                    const matrixInternal = element.querySelector('.ph-matrix__container');
                    if (matrixInternal && element.scrollHeight > 370) {
                        matrixInternal.style.padding = '0 12px 0px 0px';
                    }
                }
            });
            this.nextAndSkipButtonBlockIds.forEach((id) => {
                const element = this.getById(this.shadowRoot, id);
                if (element) {
                    const nextButton = element.querySelector('.ph-btn--primary');
                    const skipButton = element.querySelector('.ph-btn--secondary');

                    if (nextButton && skipButton) {
                        let widthNextBtn = nextButton.scrollWidth;
                        let widthSkipBtn = skipButton.scrollWidth;

                        const hasHorizontalOverflowNextButton = widthNextBtn > nextButton.clientWidth;
                        const hasHorizontalOverflowSkipButton = widthSkipBtn > skipButton.clientWidth;

                        if (hasHorizontalOverflowNextButton || hasHorizontalOverflowSkipButton) {
                            element.style.flexDirection = 'column';
                            skipButton.style.marginTop = '12px';
                            element.style.gap = '0px';
                        } else {
                            element.style.flexDirection = 'row';
                            skipButton.style.marginTop = '16px';
                            element.style.gap = '8px';
                        }
                    }
                }
            });
        });

        this.observer.observe(this.shadowRoot, { childList: true, subtree: true });
    }

    renderMiniIcon(isAtBeginning = true) {
        this.attachHost();
        this.reportDisplayDecision(true);
        let minimizedSurvey = document.createElement('button');
        minimizedSurvey.classList.add('ph-survey');
        minimizedSurvey.classList.add('ph-survey-minimized');

        const style = this.surveyData.style;
        let isSpeechBubble = style.showAsSpeechBubble === true || style.showAsSpeechBubble === '1' || style.showAsSpeechBubble === undefined;
        if ((style.keepMinimizedOnPageChange === true || style.keepMinimizedOnPageChange === '1') &&
            sessionStorage.getItem(`ph_survey_redirected_${this.surveyId}`) === 'true') {
            isSpeechBubble = false;
        }

        // The badge position settings always win so the bubble sits at the same
        // spot whether the survey starts minimized or is minimized by the user
        const vertPos = style.minimizedBadgeVerticalPosition || style.verticalPosition || 'bottom';
        const horzPos = style.minimizedBadgeHorizontalPosition || style.position || 'right';
        const tailOnLeft = isSpeechBubble && horzPos === 'left';

        if (this.surveyType === 'preview') {
            minimizedSurvey.classList.add('ph-survey-minimized--preview');
        }

        if (isSpeechBubble) {
            minimizedSurvey.classList.add('ph-survey-minimized--bubble');
            if (tailOnLeft) {
                minimizedSurvey.classList.add('ph-survey-minimized--tail-left');
            }
        } else {
            minimizedSurvey.classList.add('ph-feedback-badge');
            minimizedSurvey.setAttribute('part', 'feedback-badge');
        }

        minimizedSurvey.classList.add(
            horzPos === 'left' ? 'ph-minimized-h--left' : horzPos === 'middle' ? 'ph-minimized-h--middle' : 'ph-minimized-h--right'
        );
        // The badge docked at the middle always sits at the bottom edge,
        // so the vertical class must not compete with it
        if (isSpeechBubble || horzPos !== 'middle') {
            minimizedSurvey.classList.add(
                vertPos === 'top' ? 'ph-minimized-v--top' : vertPos === 'middle' ? 'ph-minimized-v--middle' : 'ph-minimized-v--bottom'
            );
        }

        minimizedSurvey.style.zIndex = style.zIndex ?? 999999999;

        // Icon Part
        let iconPartHtml = '';
        if (style.minimizedIcon) {
            if (style.minimizedIcon.startsWith('<')) {
                iconPartHtml = `<div class="ph-minimized-icon">${style.minimizedIcon}</div>`;
            } else {
                iconPartHtml = `<img src="${resolveImageUrl(style.minimizedIcon)}" class="ph-minimized-icon" alt="" />`;
            }
        } else {
            if (isSpeechBubble) {
                iconPartHtml = `
                    <svg class="ph-minimized-icon" viewBox="10 13 33 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M30.0614 13.2393H11.8571C11.4182 13.2393 10.9973 13.4136 10.6869 13.724C10.3765 14.0343 10.2022 14.4553 10.2022 14.8942V31.4436C10.2003 31.759 10.2895 32.0683 10.459 32.3343C10.6285 32.6003 10.8712 32.8118 11.1579 32.9434C11.377 33.0451 11.6156 33.098 11.8571 33.0985C12.2456 33.0976 12.6212 32.9591 12.9173 32.7075L12.9266 32.7003L16.3048 29.7886H30.0614C30.5003 29.7886 30.9213 29.6143 31.2316 29.3039C31.542 28.9935 31.7164 28.5726 31.7164 28.1337V14.8942C31.7164 14.4553 31.542 14.0343 31.2316 13.724C30.9213 13.4136 30.5003 13.2393 30.0614 13.2393ZM30.0614 28.1337H15.9945C15.7958 28.1338 15.6038 28.2054 15.4535 28.3354L11.8571 31.4436V14.8942H30.0614V28.1337ZM16.8219 19.859C16.8219 19.6395 16.9091 19.4291 17.0643 19.2739C17.2195 19.1187 17.4299 19.0315 17.6494 19.0315H24.2691C24.4886 19.0315 24.6991 19.1187 24.8543 19.2739C25.0094 19.4291 25.0966 19.6395 25.0966 19.859C25.0966 20.0785 25.0094 20.2889 24.8543 20.4441C24.6991 20.5993 24.4886 20.6865 24.2691 20.6865H17.6494C17.4299 20.6865 17.2195 20.5993 17.0643 20.4441C16.9091 20.2889 16.8219 20.0785 16.8219 19.859ZM16.8219 23.1689C16.8219 22.9494 16.9091 22.739 17.0643 22.5838C17.2195 22.4286 17.4299 22.3414 17.6494 22.3414H24.2691C24.4886 22.3414 24.6991 22.4286 24.8543 22.5838C25.0094 22.739 25.0966 22.9494 25.0966 23.1689C25.0966 23.3883 25.0094 23.5988 24.8543 23.754C24.6991 23.9092 24.4886 23.9964 24.2691 23.9964H17.6494C17.4299 23.9964 17.2195 23.9092 17.0643 23.754C16.9091 23.5988 16.8219 23.3883 16.8219 23.1689Z" fill="var(--ph-text-color)" />
                        <circle cx="33.3716" cy="27.9502" r="8.09081" fill="var(--ph-bg-color)"/>
                        <path d="M33.3716 18.5718C35.8577 18.5745 38.2415 19.5638 39.9995 21.3218C41.7573 23.0798 42.7459 25.4636 42.7485 27.9497L42.7427 28.2974C42.6784 30.0303 42.1343 31.714 41.1685 33.1597C40.138 34.7019 38.6731 35.904 36.9595 36.6138C35.246 37.3235 33.3605 37.5097 31.5415 37.1479C29.7225 36.7861 28.0512 35.8929 26.7397 34.5815C25.4283 33.2701 24.5352 31.5988 24.1733 29.7798C23.8115 27.9606 23.9967 26.0744 24.7065 24.3608C25.4163 22.6474 26.6186 21.1833 28.1606 20.1528C29.7028 19.1224 31.5159 18.5718 33.3706 18.5718H33.3716ZM36.0181 21.5601C34.7542 21.0365 33.3627 20.8986 32.021 21.1655C30.6794 21.4324 29.4472 22.0918 28.48 23.0591C27.5127 24.0263 26.8533 25.2585 26.5864 26.6001C26.3195 27.9418 26.4574 29.3333 26.981 30.5972C27.5045 31.8607 28.3906 32.9408 29.5278 33.7007C30.6652 34.4607 32.0027 34.8667 33.3706 34.8667L33.3716 34.8579C35.4223 34.7712 37.0436 34.0541 38.2593 32.8384C39.556 31.5417 40.2854 29.7835 40.2876 27.9497L40.2827 27.6938C40.2354 26.4155 39.8341 25.1733 39.1216 24.1069C38.3617 22.9697 37.2816 22.0836 36.0181 21.5601ZM29.813 28.7407C29.9776 28.7207 30.1449 28.7336 30.3042 28.7798C30.4635 28.826 30.6123 28.9043 30.7407 29.0093C30.8629 29.1092 30.9637 29.2324 31.0396 29.3706H31.0405C31.5847 30.3111 32.4076 30.7924 33.3706 30.7925C34.3335 30.7925 35.1562 30.3104 35.6997 29.3706C35.8629 29.0883 36.1318 28.8828 36.4468 28.7983C36.7619 28.7139 37.0978 28.7575 37.3804 28.9204C37.6631 29.0836 37.8701 29.3532 37.9546 29.6685C38.0284 29.9442 38.0037 30.2358 37.8872 30.4937L37.8315 30.6021C36.866 32.2713 35.2374 33.2544 33.3706 33.2544C31.5113 33.2543 29.8903 32.2798 28.9233 30.6216V30.6226C28.8343 30.4825 28.7745 30.3254 28.7476 30.1616C28.7206 29.998 28.7267 29.8305 28.7661 29.6694C28.8056 29.5084 28.8776 29.3568 28.9771 29.2241C29.0766 29.0913 29.2025 28.98 29.3462 28.897C29.4898 28.814 29.6484 28.7608 29.813 28.7407ZM29.7144 24.8022C30.0012 24.6834 30.3171 24.6519 30.6216 24.7124C30.926 24.773 31.2058 24.9227 31.4253 25.1421C31.6448 25.3616 31.7943 25.6414 31.855 25.9458C31.9155 26.2503 31.8849 26.5662 31.7661 26.853C31.6473 27.1399 31.4461 27.3856 31.188 27.5581C30.9299 27.7305 30.6263 27.8227 30.3159 27.8228C29.8996 27.8228 29.5 27.6571 29.2056 27.3628C28.9111 27.0683 28.7456 26.6688 28.7456 26.2524C28.7456 25.9419 28.8377 25.6386 29.0103 25.3804C29.1827 25.1223 29.4276 24.9211 29.7144 24.8022ZM36.4263 24.6821C36.8426 24.6822 37.2422 24.8477 37.5366 25.1421C37.831 25.4365 37.9966 25.8361 37.9966 26.2524C37.9966 26.5628 37.9043 26.8664 37.7319 27.1245C37.5594 27.3827 37.3137 27.5838 37.0269 27.7026C36.74 27.8215 36.4242 27.8531 36.1196 27.7925C35.8151 27.7319 35.5355 27.5823 35.3159 27.3628C35.0964 27.1433 34.9468 26.8635 34.8862 26.5591C34.8257 26.2546 34.8564 25.9387 34.9751 25.6519C35.0939 25.365 35.296 25.1193 35.5542 24.9468C35.8123 24.7744 36.1159 24.6821 36.4263 24.6821Z" fill="var(--ph-text-color)" stroke="var(--ph-bg-color)" stroke-width="0.5" />
                    </svg>
                `;
            } else {
                iconPartHtml = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="16" viewBox="0 0 23 16" fill="none">
                        <path d="M13.3333 0.888672H2.33327C2.06805 0.888672 1.8137 0.994029 1.62616 1.18157C1.43863 1.3691 1.33327 1.62346 1.33327 1.88867V11.8887C1.33214 12.0793 1.38602 12.2662 1.48846 12.4269C1.59089 12.5877 1.73753 12.7154 1.91077 12.7949C2.04316 12.8564 2.18731 12.8884 2.33327 12.8887C2.56802 12.8881 2.79499 12.8044 2.97389 12.6524L2.97952 12.648L5.02077 10.8887H13.3333C13.5985 10.8887 13.8528 10.7833 14.0404 10.5958C14.2279 10.4082 14.3333 10.1539 14.3333 9.88867V1.88867C14.3333 1.62346 14.2279 1.3691 14.0404 1.18157C13.8528 0.994029 13.5985 0.888672 13.3333 0.888672ZM13.3333 9.88867H4.83327C4.71321 9.88874 4.59719 9.932 4.50639 10.0105L2.33327 11.8887V1.88867H13.3333V9.88867ZM5.33327 4.88867C5.33327 4.75606 5.38595 4.62889 5.47972 4.53512C5.57348 4.44135 5.70066 4.38867 5.83327 4.38867H9.83327C9.96588 4.38867 10.0931 4.44135 10.1868 4.53512C10.2806 4.62889 10.3333 4.75606 10.3333 4.88867C10.3333 5.02128 10.2806 5.14846 10.1868 5.24223C10.0931 5.33599 9.96588 5.38867 9.83327 5.38867H5.83327C5.70066 5.38867 5.57348 5.33599 5.47972 5.24223C5.38595 5.14846 5.33327 5.02128 5.33327 4.88867ZM5.33327 6.88867C5.33327 6.75606 5.38595 6.62889 5.47972 6.53512C5.57348 6.44135 5.70066 6.38867 5.83327 6.38867H9.83327C9.96588 6.38867 10.0931 6.44135 10.1868 6.53512C10.2806 6.62889 10.3333 6.75606 10.3333 6.88867C10.3333 7.02128 10.2806 7.14846 10.1868 7.24223C10.0931 7.33599 9.96588 7.38867 9.83327 7.38867H5.83327C5.70066 7.38867 5.57348 7.33599 5.47972 7.24223C5.38595 7.14846 5.33327 7.02128 5.33327 6.88867Z" fill="var(--ph-text-color)" stroke="var(--ph-text-color)" stroke-width="0.222222"/>
                        <circle cx="15.3332" cy="9.77756" r="4.88889" fill="var(--ph-text-color)"/>
                        <path d="M15.334 4.11133C16.8362 4.11299 18.2766 4.71024 19.3389 5.77246C20.4011 6.83474 20.9984 8.27511 21 9.77734L20.9961 9.9873C20.9573 11.0346 20.6287 12.0521 20.0449 12.9258C19.4223 13.8576 18.5373 14.5838 17.502 15.0127C16.4665 15.4416 15.3268 15.5546 14.2275 15.3359C13.1284 15.1173 12.1186 14.5766 11.3262 13.7842C10.5339 12.9917 9.99401 11.9819 9.77539 10.8828C9.55687 9.78383 9.66893 8.64461 10.0977 7.60938C10.5266 6.57393 11.2537 5.68907 12.1855 5.06641C13.1173 4.44386 14.2124 4.11139 15.333 4.11133H15.334ZM16.9326 5.91602C16.169 5.59976 15.3282 5.51746 14.5176 5.67871C13.707 5.84003 12.9623 6.23785 12.3779 6.82227C11.7935 7.40668 11.3957 8.15133 11.2344 8.96191C11.0731 9.77258 11.1554 10.6133 11.4717 11.377C11.788 12.1407 12.3244 12.7937 13.0117 13.2529C13.6989 13.712 14.5066 13.957 15.333 13.957L15.54 13.9521C16.5728 13.8998 17.5525 13.4661 18.2871 12.7314C19.0707 11.9479 19.5114 10.8855 19.5127 9.77734L19.501 9.46875C19.4478 8.75106 19.2103 8.05737 18.8086 7.45605C18.3493 6.76874 17.6963 6.23235 16.9326 5.91602ZM13.1836 10.2549C13.2831 10.2428 13.3842 10.2514 13.4805 10.2793C13.5768 10.3072 13.6665 10.3545 13.7441 10.418C13.8182 10.4786 13.8799 10.5528 13.9258 10.6367L14.0566 10.8369C14.38 11.273 14.8241 11.496 15.333 11.4961C15.9147 11.4961 16.4118 11.2043 16.7402 10.6367C16.8388 10.4659 17.0019 10.3411 17.1924 10.29C17.3828 10.2391 17.5861 10.2657 17.7568 10.3643C17.9273 10.4628 18.0514 10.6253 18.1025 10.8154C18.1536 11.0059 18.1269 11.2091 18.0283 11.3799V11.3809C17.4449 12.3893 16.4609 12.9824 15.333 12.9824C14.2053 12.9823 13.2221 12.3898 12.6387 11.3809V11.3799C12.5891 11.2985 12.5555 11.2084 12.54 11.1143C12.5238 11.0153 12.5269 10.9138 12.5508 10.8164C12.5746 10.719 12.6186 10.6271 12.6787 10.5469C12.7388 10.4668 12.8147 10.3997 12.9014 10.3496C12.9881 10.2995 13.0841 10.267 13.1836 10.2549ZM13.6719 7.82129C13.8559 7.8579 14.0255 7.94837 14.1582 8.08105C14.2909 8.21373 14.3814 8.38336 14.418 8.56738C14.4544 8.75127 14.435 8.94203 14.3633 9.11523C14.2915 9.2884 14.1705 9.43683 14.0146 9.54102C13.8587 9.64524 13.6749 9.70115 13.4873 9.70117C13.2357 9.70117 12.9943 9.60076 12.8164 9.42285C12.6385 9.24493 12.5381 9.00357 12.5381 8.75195C12.5381 8.56434 12.594 8.3806 12.6982 8.22461C12.8024 8.06876 12.9508 7.94777 13.124 7.87598C13.2972 7.80423 13.488 7.7848 13.6719 7.82129ZM17.1797 7.80371C17.4312 7.80376 17.6727 7.90318 17.8506 8.08105C18.0284 8.25895 18.1279 8.50039 18.1279 8.75195C18.1279 8.93957 18.073 9.1233 17.9688 9.2793C17.8645 9.43524 17.7162 9.55709 17.543 9.62891C17.3696 9.70071 17.1782 9.71922 16.9941 9.68262C16.8102 9.64599 16.6414 9.55544 16.5088 9.42285C16.3762 9.29026 16.2857 9.12139 16.249 8.9375C16.2124 8.75347 16.2309 8.56203 16.3027 8.38867C16.3745 8.21539 16.4964 8.0671 16.6523 7.96289C16.8083 7.85869 16.9921 7.80371 17.1797 7.80371Z" fill="var(--ph-bg-color)" stroke="var(--ph-text-color)" stroke-width="0.666667"/>
                    </svg>
                `;            
            }
        }

        // Text Part
        const textToDisplay = style.minimizedText !== undefined && style.minimizedText !== null ? style.minimizedText : 'Feedback';
        const isVerticalBadge = !isSpeechBubble && horzPos !== 'middle';
        const textPartHtml = isSpeechBubble
            ? `<div class="ph-minimized-text ph-minimized-text--truncate">${textToDisplay}</div>`
            : `<div class="ph-feedback-text ph-minimized-text${isVerticalBadge ? ' ph-minimized-text--vertical' : ''}">${textToDisplay}</div>`;

        // Tail Part — the tail sits on the side the bubble is anchored to
        const tailHtml = isSpeechBubble
            ? `<svg class="ph-minimized-tail${tailOnLeft ? ' ph-minimized-tail--left' : ''}" width="12" height="6">
            <path d="${tailOnLeft ? 'M0 6V0H12L0 6Z' : 'M12 6V0H0L12 6Z'}" fill="var(--ph-bg-color)"/>
        </svg>`
            : '';

        minimizedSurvey.innerHTML = isSpeechBubble ? `${iconPartHtml}${textPartHtml}${tailHtml}` : `${textPartHtml}${iconPartHtml}${tailHtml}`;

        minimizedSurvey.setAttribute('role', 'button');
        minimizedSurvey.setAttribute('tabindex', '0');
        minimizedSurvey.setAttribute('aria-label', 'Open survey');
        minimizedSurvey.addEventListener('click', () => {
            // The badge stays until the server allowed the survey (pending gate)
            this.openSurvey(() => {
                if (this.storage) this.storage.remove(`ph_survey_minimized_${this.surveyId}`);
                minimizedSurvey.style.display = 'none';
                let wrapper = this.shadowRoot.querySelector('.ph-wrapper');
                if (wrapper) wrapper.style.display = '';
                let externalBox = this.shadowRoot.querySelector('.ph-overlay');
                if (externalBox) externalBox.style.display = '';

                if (this.hostElement) {
                    this.hostElement.dispatchEvent(new CustomEvent('pollhero-survey-opened', { bubbles: true, composed: true }));
                }

                this.init();
            });
        });
        minimizedSurvey.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                minimizedSurvey.click();
            }
        });
        
        let wrapper = this.shadowRoot.querySelector('.ph-wrapper');
        if (wrapper) wrapper.style.display = 'none';
        let externalBox = this.shadowRoot.querySelector('.ph-overlay');
        if (externalBox) externalBox.style.display = 'none';
        
        this.shadowRoot.appendChild(minimizedSurvey);
        
        if (this.hostElement) {
            this.hostElement.dispatchEvent(new CustomEvent('pollhero-survey-minimized', { 
                detail: { isAtBeginning }, 
                bubbles: true, 
                composed: true 
            }));
        }
    }

    getPopupSurveyContainer() {
        this.shadowRoot = this.hostElement.attachShadow({ mode: 'open' });
        this.shadowRoot.innerHTML += getStyleData(this.surveyData.style, this.surveyType, {}, this.can);

        if (this.surveyData.style.showOverlay === '1' || this.surveyData.style.showOverlay === true) {
            let externalBox = document.createElement('div');
            externalBox.classList.add('ph-overlay');
            this.shadowRoot.appendChild(externalBox);
            this.container = document.createElement('div');
            this.container.className = 'ph-wrapper';
            this.container.setAttribute('role', 'dialog');
            this.container.setAttribute('aria-modal', 'true');
            this.container.setAttribute('aria-label', 'Survey');
            this.container.setAttribute('aria-live', 'polite');
            externalBox.appendChild(this.container);
        } else {
            this.container = document.createElement('div');
            this.container.className = 'ph-wrapper';
            this.container.setAttribute('role', 'dialog');
            this.container.setAttribute('aria-modal', 'true');
            this.container.setAttribute('aria-label', 'Survey');
            this.container.setAttribute('aria-live', 'polite');
            this.shadowRoot.appendChild(this.container);
        }
        this.addObserver();
    }

    getCssSelectorEmbedSurveyContainer() {
        const enabled = this.surveyData?.injection_settings?.enableCSSSelector;

        if (enabled === false || enabled === 0 || enabled === '0') {
            console.warn('[Survey] Injection via CSS selector is disabled.');
            this.injectionFailed = true;
            return;
        }

        const positionLabels = ['replace', 'atFirst', 'atLast', 'before', 'after'];
        const position = this.surveyData?.injection_settings?.position || 'replace';
        const selector = this.surveyData?.injection_settings?.cssSelector;

        if (!positionLabels.includes(position)) {
            console.warn('[Survey] Invalid embed position:', position);
            this.injectionFailed = true;
            return;
        }

        if (!selector) {
            console.warn('[Survey] CSS selector is required for selector injection.');
            this.injectionFailed = true;
            return;
        }

        let parentElement = null;
        try {
            parentElement = selector ? document.querySelector(selector) : null;
        } catch {
            console.warn('[Survey] Invalid CSS selector:', selector);
            this.injectionFailed = true;
            return;
        }

        if (!parentElement) {
            this.waitForCssSelectorTarget(selector, position);
            return;
        }

        this.mountCssSelectorEmbed(parentElement, position);
    }

    waitForCssSelectorTarget(selector, position) {
        this.pendingInjection = true;
        // The launcher gets its engine now; a missing target aborts later
        this.reportDisplayDecision(true);

        const findAndMount = () => {
            let target = null;
            try {
                target = document.querySelector(selector);
            } catch {
                // The selector was already validated before observation began.
            }

            if (!target) return false;

            cleanup();
            this.pendingInjection = false;
            this.mountCssSelectorEmbed(target, position);
            this.startSurvey();
            return true;
        };

        const cleanup = () => {
            if (this.cssSelectorTargetObserver) {
                this.cssSelectorTargetObserver.disconnect();
                this.cssSelectorTargetObserver = null;
            }
            if (this.cssSelectorTargetTimeout) {
                clearTimeout(this.cssSelectorTargetTimeout);
                this.cssSelectorTargetTimeout = null;
            }
        };

        const fail = () => {
            cleanup();
            this.pendingInjection = false;
            this.injectionFailed = true;
            SurveyEngine.activeSurveys.delete(this.surveyId);
            this.hostElement.remove();
            console.warn('[Survey] CSS selector not found after waiting:', selector);
        };

        if (typeof MutationObserver === 'undefined' || !document.documentElement) {
            fail();
            return;
        }

        this.cssSelectorTargetObserver = new MutationObserver(findAndMount);
        this.cssSelectorTargetObserver.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });
        this.cssSelectorTargetTimeout = setTimeout(fail, SurveyEngine.CSS_SELECTOR_WAIT_TIMEOUT);

        // Cover the small gap between the initial lookup and observer setup.
        findAndMount();
    }

    mountCssSelectorEmbed(parentElement, position) {

        switch (position) {
            case 'replace':
                // The old content is cleared in commitReplace() once the survey
                // is allowed to show, so a display: false leaves the page intact
                this.replaceTarget = parentElement;
                parentElement.appendChild(this.hostElement);
                break;

            case 'atFirst':
                parentElement.insertAdjacentElement('afterbegin', this.hostElement);
                break;

            case 'atLast':
                parentElement.insertAdjacentElement('beforeend', this.hostElement);
                break;

            case 'before':
                parentElement.insertAdjacentElement('beforebegin', this.hostElement);
                break;

            case 'after':
                parentElement.insertAdjacentElement('afterend', this.hostElement);
                break;
        }

        this.shadowRoot = this.hostElement.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML += getStyleData(
            this.surveyData.style,
            this.surveyType,
            {},
            this.can
        );

        this.container = document.createElement('div');
        this.container.className = 'ph-wrapper';
        this.container.style.width = '100%';
        this.container.setAttribute('aria-live', 'polite');
        this.shadowRoot.appendChild(this.container);

        this.addObserver();
    }

    commitReplace() {
        const target = this.replaceTarget;
        if (!target) return;
        this.replaceTarget = null;
        Array.from(target.childNodes).forEach((node) => {
            if (node !== this.hostElement) node.remove();
        });
    }

    getEmbedSurveyContainer() {
        this.shadowRoot = this.hostElement.attachShadow({ mode: 'open' });
        this.shadowRoot.innerHTML += getStyleData(this.surveyData.style, this.surveyType, {}, this.can);

        this.container = document.createElement('div');
        this.container.className = 'ph-wrapper';
        this.container.setAttribute('aria-live', 'polite');
        this.shadowRoot.appendChild(this.container);
        this.addObserver();
    }

    getStandaloneSurveyContainer() {
        this.hostElement.classList.add('ph-survey-host');
        this.hostElement.style.width = '100%';
        this.hostElement.style.height = '100%';
        this.hostElement.style.position = 'fixed';
        this.hostElement.style.top = '0';

        const bg = this.surveyData.style.background;
        const enableBgImage = bg?.enableBackgroundImage === '1' || bg?.enableBackgroundImage === true;
        const bgPosition = bg?.backgroundImagePosition || 'background';
        if (enableBgImage && (bgPosition === 'left' || bgPosition === 'right')) {
            this.hostElement.classList.add('ph-survey-host--side-image');
        } else if (enableBgImage) {
            this.hostElement.classList.add('ph-survey-host--full-image');
        }

        this.hostElement.innerHTML += getStyleData(this.surveyData.style, this.surveyType, {}, this.can);

        let leftElement = document.createElement('div');
        leftElement.className = 'ph-bg--left';
        this.hostElement.appendChild(leftElement);

        const bgBlock = document.createElement('div');
        bgBlock.className = 'ph-bg--main';
        this.hostElement.appendChild(bgBlock);

        const boxOuter = document.createElement('div');
        boxOuter.className = 'ph-box-outer';

        const boxInner = document.createElement('div');
        boxInner.className = 'ph-box-inner';

        const titleText = document.createElement('div');
        titleText.className = 'ph-title';
        titleText.textContent = this.surveyData.style.standaloneInfo?.title ?? '';

        const subtitleText = document.createElement('div');
        subtitleText.className = 'ph-subtitle';
        subtitleText.textContent = this.surveyData.style.standaloneInfo?.subtitle ?? '';

        boxInner.appendChild(titleText);
        boxInner.appendChild(subtitleText);

        this.container = document.createElement('div');
        this.container.className = 'ph-wrapper';
        this.container.setAttribute('aria-live', 'polite');
        this.container.style.width = '100%';

        const extraBtnContainer = document.createElement('div');
        extraBtnContainer.className = 'ph-extra-btn-container';

        const extraBtn = document.createElement('button');
        extraBtn.className = 'ph-extra-btn';
        extraBtn.textContent = this.surveyData.style.standaloneInfo?.extraButtonText ?? '';
        extraBtn.onclick = () => {
            const link = this.surveyData.style.standaloneInfo?.extraButtonLink;
            if (link) window.location.href = link;
        };

        extraBtnContainer.appendChild(extraBtn);

        boxOuter.appendChild(boxInner);
        boxOuter.appendChild(this.container);
        boxOuter.appendChild(extraBtnContainer);
        bgBlock.appendChild(boxOuter);

        let rightElement = document.createElement('div');
        rightElement.className = 'ph-bg--right';
        this.hostElement.appendChild(rightElement);
        this.shadowRoot = this.hostElement;

        this.addObserver();
    }

    getPreviewSurveyContainer() {
        if (!this.hostElement) return;
        if (!this.shadowRoot) {
            this.shadowRoot = this.hostElement.attachShadow({ mode: 'open' });
        }
        this.shadowRoot.innerHTML += getStyleData(this.surveyData.style, this.surveyType, { staticLayout: this.staticLayout, previewType: this.previewType }, this.can);

        this.container = document.createElement('div');
        this.container.className = 'ph-wrapper';
        this.container.setAttribute('aria-live', 'polite');
        this.shadowRoot.appendChild(this.container);
        this.addObserver();
    }

    init() {
        if (this.surveyType === 'preview' || this.isLinkPreview() || this.surveyType === 'previewStandalone') {
            this.answersLength = this.answerManager.getAnswersLength(this.questions);
            this.renderCurrentQuestion();
            this.isPreviewMode = true;
        } else {
            this.answersLength = this.answerManager.getAnswersLength(this.questions);
            // Only the first init builds the answer list: a reopen after
            // minimize must keep the answers already given, otherwise the
            // final complete is blocked by "empty" required answers
            if (!this.answersInitialized) {
                this.answerManager.initializeAnswers(this.questions);
                this.answersInitialized = true;
            }
            this.renderCurrentQuestion();
            if (!this.viewTracked) {
                this.notifySurveyViewed();
            }
        }
        if (this.surveyType === 'onsite' || this.surveyType === 'previewLink') {
            this.setupFocusTrap();
        }
    }

    setupFocusTrap() {
        this._focusTrapHandler = (e) => {
            // Only act when focus is actually inside the survey
            const active = this.shadowRoot.activeElement;
            if (!active) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                // Same behaviour tracking as the close button in the header
                const question = this.currentQuestionInstance?.question;
                if (question) this.trackBehavior(question, 'closed');
                this.closeSurvey();
                return;
            }
            if (e.key !== 'Tab') return;

            const wrapper = this.shadowRoot.querySelector('.ph-wrapper');
            if (!wrapper || !wrapper.contains(active)) return;

            const focusable = wrapper.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (active === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (active === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        this.shadowRoot.addEventListener('keydown', this._focusTrapHandler);
    }

    removeFocusTrap() {
        if (this._focusTrapHandler) {
            this.shadowRoot.removeEventListener('keydown', this._focusTrapHandler);
            this._focusTrapHandler = null;
        }
    }

    setInitialIndex() {
        let active = this.questions[0].properties
            ? this.questions[0].properties.isActive
            : this.questions[0].isActive;
        if (active === false || active === '0') {
            this.startIndex = 1;
            if (this.currentIndex === 0) {
                this.currentIndex = 1;
            }
        }
    }

    renderCurrentQuestion() {
        if (this.currentIndex === null) return;

        const qData = this.surveyData.questions[this.currentIndex];
        if (!qData) {
            // Advanced past the last question (no Thank You page configured):
            // the survey is finished and must close instead of freezing
            if (this.currentIndex >= this.questions.length) {
                this.closeSurvey(false);
            }
            return;
        }

        const qProps = qData.properties ?? qData;
        const qType = qProps.type;
        const qActive = qProps.isActive;

        if (qType === 'Thank You Message' && (qActive === '0' || qActive === false)) {
            let hasActiveAhead = false;
            for (let i = this.currentIndex + 1; i < this.questions.length; i++) {
                const q = this.questions[i];
                const props = q.properties ?? q;
                if (props.isActive !== '0' && props.isActive !== false) {
                    hasActiveAhead = true;
                    break;
                }
            }
            if (hasActiveAhead) {
                this.currentIndex++;
                this.renderCurrentQuestion();
            } else {
                this.closeSurvey(false);
            }
            return;
        }

        if (this.currentQuestionInstance) {
            this.currentQuestionInstance.unmount();
        }

        const ctx = this.createContext();
        this.currentQuestionInstance = createQuestion(qData.properties ?? qData, ctx);

        if (this.currentQuestionInstance) {
            this.currentQuestionInstance.mount(this.shadowRoot.querySelector('.ph-wrapper'));

            const qProps = qData.properties ?? qData;
            const questionId = qProps.id;
            const questionType = qProps.type;

            if (questionType === 'Thank You Message' && (this.isRedirectedDirectlyToThankYou || this.questionHistory.length === 0)) {
                this.hostElement.classList.add('ph-survey-host--thank-you');
            } else {
                this.hostElement.classList.remove('ph-survey-host--thank-you');
            }

            if (this.currentIndex > 0 && !this.isPreviewMode) {
                this.answerManager.updateViewedStatus(questionId);
            }

            if (!this.isPreviewMode) {
                if (questionType === 'Thank You Message') {
                    this.completeSurveyToBackend();
                }

                this.notifyQuestionViewed(questionId);
            }
            // Only auto-focus on question navigation (not initial render)
            // to avoid stealing focus from the host page when the survey appears
            if (this.questionHistory.length > 0) {
                requestAnimationFrame(() => {
                    const title = this.shadowRoot.querySelector('.ph-question__title');
                    if (title) {
                        title.setAttribute('tabindex', '-1');
                        title.focus();
                    }
                });
            }
        }
    }

    goNext(question, lastAnswer) {
        this.updateAnswers(question, lastAnswer);
        this.sendQuestionResponse(question, lastAnswer);
        this.updateLocalStorageOnAnswer();
        this.questionHistory.push(this.currentIndex);

        if (question.type === 'Thank You Message' || question.type === 'Engagement Trigger') {
            this.currentIndex++;
        } else {
            const result = checkRouteLogic(lastAnswer, question, this.questions, this.currentIndex);
            if (result) {
                if (result.closeSurvey) {
                    if (this.surveyType === 'preview') return;
                    this.currentIndex = null;
                    this.answerManager.updateSkippedStatus(null, this.questions);
                    // The logic finished the survey: always close (and complete),
                    // never minimize to a badge that would reopen an empty survey
                    this.closeSurvey(false);
                    return;
                }
                this.currentIndex = result.nextIndex;
                if (result.skippedIds.length > 0) {
                    this.answerManager.updateSkippedStatus(result.skippedIds[0], this.questions);
                }
            }
        }

        // Fire complete as soon as the last real question is answered,
        // even if a Thank You page follows.
        const nextQ = this.surveyData.questions[this.currentIndex];
        if (nextQ) {
            const nextType = (nextQ.properties ?? nextQ).type;
            if (nextType === 'Thank You Message' || nextType === 'Engagement Trigger') {
                this.completeSurveyToBackend();
            }
        }

        this.renderCurrentQuestion();
    }

    trackBehavior(question, behavior) {
        if (this.isPreviewMode) return;
        trackBehavior(CONFIG.apiUrl, question.id, {
            user_id: this.userId,
            question_id: question.id,
            behavior: behavior,
            ...this.getSessionPayload(),
        })
            .then((response) => this.adoptSessionFromResponse(response))
            .catch(() => { });
    }

    // ---- backend session -------------------------------------------------

    getSessionPayload() {
        return this.sessionId !== null && this.sessionId !== undefined ? { session_id: this.sessionId } : {};
    }

    setSessionId(sessionId) {
        const id = normalizeSessionId(sessionId);
        if (id === null) return;
        this.sessionId = id;
        // Once complete was sent the session is closed on the server. Trailing
        // calls (Thank You view, close behaviour) still reference it in memory,
        // but it must not survive for the next surveys/view of this survey.
        if (!this.hasCalledComplete) {
            storeSessionId(this.surveyId, id);
        }
    }

    // The id in the latest server answer always wins over the remembered one
    adoptSessionFromResponse(response) {
        if (!response || typeof response.text !== 'function') return Promise.resolve();
        return response
            .text()
            .then((text) => {
                if (!text) return;
                const data = JSON.parse(text);
                if (data && data.session_id !== undefined) {
                    this.setSessionId(data.session_id);
                }
            })
            .catch(() => { });
    }

    discardSession() {
        clearStoredSessionId(this.surveyId);
    }

    goBack() {
        if (this.questionHistory.length > 0) {
            this.currentIndex = this.questionHistory.pop();
            this.renderCurrentQuestion();
        }
    }

    updateAnswers(question, answer) {
        this.answerManager.updateAnswer(question, answer);
    }

    closeOverlay() {
        if (
            (this.surveyType === 'onsite' || this.surveyType === 'previewLink') &&
            (this.surveyData.style.showOverlay === '1' || this.surveyData.style.showOverlay === true)
        ) {
            let externalBox = this.shadowRoot.querySelector('.ph-overlay');
            if (externalBox) {
                externalBox.style.display = 'none';
            }
        }
        this.removeFocusTrap();
        if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
            this.previouslyFocusedElement.focus();
        }
    }

    markSurveyViewSent() {
        if (!this.storage) return;
        let viewedSurveys = this.storage.get('ph_survey_viewed') ?? [];
        if (!Array.isArray(viewedSurveys)) viewedSurveys = [];
        if (!viewedSurveys.includes(this.surveyId)) {
            viewedSurveys.push(this.surveyId);
        }
        this.storage.set('ph_survey_viewed', viewedSurveys);
    }

    // Question views are deduplicated per engine instance only. Participations
    // are server-side sessions, so a later session of the same survey must send
    // its own question views again; a persistent list would suppress them.
    hasSentQuestionView(questionId) {
        return this.viewedQuestionIds.has(questionId);
    }

    markQuestionViewSent(questionId) {
        this.viewedQuestionIds.add(questionId);
    }

    notifyQuestionViewed(questionId) {
        if (this.isPreviewMode) return;
        if (this.hasSentQuestionView(questionId)) return;

        const sendViewRequest = () => {
            if (this.hasSentQuestionView(questionId)) return;
            this.markQuestionViewSent(questionId);
            viewQuestion(CONFIG.apiUrl, questionId, {
                user_id: this.userId,
                ...this.getSessionPayload(),
            })
                .then((response) => this.adoptSessionFromResponse(response))
                .catch(() => { });
        };

        const target = this.shadowRoot?.querySelector('.ph-survey') ?? this.hostElement;
        this.whenVisible(target, SurveyEngine.VIEW_THRESHOLD, sendViewRequest);
    }

    sendQuestionResponse(question, answer) {
        if (this.isPreviewMode) return;
        if (question.type === 'Thank You Message' || question.type === 'Engagement Trigger') return;

        respondQuestion(CONFIG.apiUrl, question.id, {
            user_id: this.userId,
            answer: answer,
            ...this.getSessionPayload(),
        })
            .then((response) => this.adoptSessionFromResponse(response))
            .catch(() => { });
    }

    updateLocalStorageOnAnswer() {
        if (this.isPreviewMode) return;
        if (this.storage) {
            let atLeastOne = this.storage.get('ph_survey_at_least_one_answer') ?? [];
            if (!atLeastOne.includes(this.surveyId)) {
                atLeastOne.push(this.surveyId);
                this.storage.set('ph_survey_at_least_one_answer', atLeastOne);
            }
        }
    }

    completeSurveyToBackend() {
        if (this.isPreviewMode) return;
        if (this.hasCalledComplete) return;
        if (!this.answerManager.checkHasAtLeastOneAnswer()) return;
        if (this.answerManager.checkHasEmptyAnswers()) return;

        this.hasCalledComplete = true;

        const payload = {
            user_id: this.userId,
            time_taken: Date.now() - this.surveyStartTime,
            ...this.getSessionPayload(),
        };

        completeSurvey(CONFIG.apiUrl, this.surveyId, payload)
            .then((response) => {
                if (this.storage) {
                    let completed = this.storage.get('ph_survey_completed') ?? [];
                    if (!completed.includes(this.surveyId)) {
                        completed.push(this.surveyId);
                    }
                    this.storage.set('ph_survey_completed', completed);
                }
                // The next surveys/view must start without the finished session,
                // otherwise (visibility "always") answers would land in it
                this.discardSession();
                return this.adoptSessionFromResponse(response);
            })
            .catch(() => { });
    }

    // Legacy path (standalone and engines created without the launcher): the
    // view is tracked after render and the session id is adopted. Every
    // participation is a server-side session, so each engine instance sends
    // its own view; the ph_survey_viewed list is only kept for the local
    // visibility shortcuts (standalone redirect with visibility "once").
    notifySurveyViewed() {
        if (this.isPreviewMode) return;
        if (this.surveyViewSent) return;

        const sendViewRequest = () => {
            if (this.surveyViewSent) return;
            this.surveyViewSent = true;
            this.markSurveyViewSent();

            viewSurvey(CONFIG.apiUrl, this.surveyId, buildSurveyViewPayload(this.userId, this.storage, this.sessionId))
                .then((response) => this.adoptSessionFromResponse(response))
                .catch(() => { });
        };

        const target = this.shadowRoot?.querySelector('.ph-survey') ?? this.hostElement;
        this.whenVisible(target, SurveyEngine.VIEW_THRESHOLD, sendViewRequest);
    }

    // allowMinimize: user-initiated closes may minimize instead (per style config);
    // a finished survey passes false so it always fully closes
    closeSurvey(allowMinimize = true) {
        if (this.isDuplicate) return;
        SurveyEngine.activeSurveys.delete(this.surveyId);

        // Closing from the Thank You page means the survey is done —
        // minimizing would re-offer an already answered survey
        const currentQ = this.questions?.[this.currentIndex];
        if (currentQ && (currentQ.properties ?? currentQ).type === 'Thank You Message') {
            allowMinimize = false;
        }

        if (allowMinimize && (this.isPreviewMode || this.surveyType === 'onsite')) {
            if (this.surveyData.style.allowCloseMinimize === true || this.surveyData.style.allowCloseMinimize === '1') {
                if (this.surveyData.style.closeType === 'minimize') {
                    if (this.currentQuestionInstance && !this.isStandalone()) {
                        this.currentQuestionInstance.unmount();
                    }
                    this.removeFocusTrap();
                    this.closeOverlay();
                    
                    if (this.surveyData.style.keepMinimizedOnPageChange === true || this.surveyData.style.keepMinimizedOnPageChange === '1') {
                        if (this.storage) this.storage.set(`ph_survey_minimized_${this.surveyId}`, true);
                    }
                    
                    this.renderMiniIcon(false);
                    return;
                }
            }
        }

        if (this.currentQuestionInstance && !this.isStandalone()) {
            this.currentQuestionInstance.unmount();
        }
        this.removeFocusTrap();
        this.closeOverlay();

        // Previews never submit; a standalone preview also never closes the tab
        if (this.isPreviewMode && !this.isLinkPreview()) return;

        this.completeSurveyToBackend();

        if (this.surveyType === 'standalone') {
            setTimeout(() => {
                window.open('', '_self', '');
                window.close();
            }, 500);
        }
    }

    // Skipping an optional question gives no answer, but its logic still
    // applies (notAnswered, notContain, "Alle anderen" ...)
    skip() {
        this.questionHistory.push(this.currentIndex);
        const qData = this.surveyData.questions[this.currentIndex];
        const question = qData ? (qData.properties ?? qData) : null;
        const result = question ? checkRouteLogic('', question, this.questions, this.currentIndex) : null;

        if (result && result.closeSurvey) {
            if (this.surveyType === 'preview') return;
            this.currentIndex = null;
            this.answerManager.updateSkippedStatus(null, this.questions);
            this.closeSurvey(false);
            return;
        }

        this.currentIndex = result ? result.nextIndex : this.currentIndex + 1;
        if (result && result.skippedIds.length > 0) {
            this.answerManager.updateSkippedStatus(result.skippedIds[0], this.questions);
        }
        this.renderCurrentQuestion();
    }
}
